// resources/js/ui/header/sidebar-menu.js
export default function sidebarMenu(options = {}) {
  const cfg = {
    trigger: options.trigger || '#menu-toggle',
    panel: options.panel || '#wk-sidebar',
    overlay: options.overlay || '#wk-sidebar-overlay',
    closeBtn: options.closeBtn || '.wk-sidebar__close',
    openClass: options.openClass || 'is-open',
    overlayClass: options.overlayClass || 'is-active',
    lockClass: options.lockClass || 'wk-scroll-lock',
  };

  const $trigger = document.querySelector(cfg.trigger);
  const $panel   = document.querySelector(cfg.panel);
  const $overlay = document.querySelector(cfg.overlay);

  if (!$panel || !$overlay) return; // nothing to do

  let lastFocused = null;
  let unbindFocusTrap = null;

  const setAria = (open) => {
    $panel.setAttribute('aria-hidden', open ? 'false' : 'true');
    if ($trigger) $trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
  };

  const lockScroll = (on) => {
    const b = document.body;
    if (on) {
      const hasScrollbar = window.innerWidth > document.documentElement.clientWidth;
      if (hasScrollbar) {
        const pr = window.innerWidth - document.documentElement.clientWidth;
        b.style.paddingRight = pr ? `${pr}px` : '';
      }
      b.classList.add(cfg.lockClass);
      b.style.overflow = 'hidden';
    } else {
      b.classList.remove(cfg.lockClass);
      b.style.overflow = '';
      b.style.paddingRight = '';
    }
  };

  const getFocusable = () => {
    const sel = [
      'a[href]',
      'area[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'details summary',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');
    return Array.from($panel.querySelectorAll(sel))
      .filter(el => el.offsetParent !== null || el === document.activeElement);
  };

  const trapFocus = () => {
    const nodes = getFocusable();
    if (!nodes.length) return () => {};
    const first = nodes[0];
    const last  = nodes[nodes.length - 1];

    const handler = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handler);
    // focus first node
    first.focus();

    return () => document.removeEventListener('keydown', handler);
  };

  const open = () => {
    if ($panel.classList.contains(cfg.openClass)) return;
    lastFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    $panel.classList.add(cfg.openClass);
    $overlay.classList.add(cfg.overlayClass);
    setAria(true);
    lockScroll(true);
    unbindFocusTrap = trapFocus();

    // close on any in-menu link click (improves perceived speed on navigation)
    $panel.addEventListener('click', linkCloseOnce, { once: true });
  };

  const close = () => {
    if (!$panel.classList.contains(cfg.openClass)) return;

    $panel.classList.remove(cfg.openClass);
    $overlay.classList.remove(cfg.overlayClass);
    setAria(false);
    lockScroll(false);
    if (typeof unbindFocusTrap === 'function') unbindFocusTrap();
    unbindFocusTrap = null;

    // return focus to trigger for a11y
    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
  };

  const toggle = () => ($panel.classList.contains(cfg.openClass) ? close() : open());

  const linkCloseOnce = (e) => {
    const a = e.target.closest('a[href]');
    if (a) close();
  };

  // Bind core events
  $trigger && $trigger.addEventListener('click', (e) => {
    e.preventDefault();
    toggle();
  });
  $overlay.addEventListener('click', close);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });

  // Bind/refresh close button(s)
  const bindCloseButtons = () => {
    $panel.querySelectorAll(cfg.closeBtn).forEach(btn => {
      btn.removeEventListener('click', close);
      btn.addEventListener('click', close);
    });
  };
  bindCloseButtons();

  // If content inside panel is ever refreshed (e.g., Woo fragments), rebind
  document.addEventListener('wc_fragments_refreshed', bindCloseButtons);

  // Public API (optional)
  return { open, close, toggle };
}
